import {
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material'
import { Box, Divider, Typography } from '@mui/material'

import { calculateDeliveryWindow } from 'constants/TrackingMap'
import { BookingAddressType, BookingStatus } from 'generated/graphql'
import { ConsignmentPair } from 'hooks/booking/useBookingAddresses'
import { AddressEta } from 'hooks/useAllAddressesEta'

interface DetailsProps {
  booking?: any
  onDetailsClick?: () => void
  bookingCurrentAction?: any
  nextBookingAction?: any
  consignmentPairs?: ConsignmentPair[]
  referenceAddressType?: BookingAddressType | null
  addressEtas?: AddressEta[]
}

const containerStyles = {
  p: 2,
  borderBottom: '2px solid #F6F4F4',
}

const titleStyles = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#333',
  mb: 2,
}

const contentContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
}

const detailRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const clickableRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  justifyContent: 'space-between',
  cursor: 'pointer',
}

const detailRowContentStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flex: 1,
}

const iconStyles = {
  fontSize: '20px',
  color: '#666',
}

const textStyles = {
  fontSize: '14px',
  color: '#333',
  flex: 1,
}

const chevronStyles = {
  color: '#858585',
}

export const Details = ({
  onDetailsClick,
  booking,
  bookingCurrentAction,
  nextBookingAction,
  consignmentPairs,
  referenceAddressType,
  addressEtas = [],
}: DetailsProps) => {
  if (consignmentPairs && consignmentPairs.length > 0) {
    const flatPairs = consignmentPairs.flatMap(({ collection, deliveries }) =>
      deliveries.length > 0
        ? deliveries.map((delivery) => ({ collection, delivery }))
        : [{ collection, delivery: null as typeof deliveries[0] | null }],
    )

    return (
      <Box sx={containerStyles}>
        <Typography variant='h6' sx={titleStyles}>
          Details
        </Typography>

        {flatPairs.map(({ collection, delivery }, index) => {
          const from = collection.address
            ? `${collection.address.addressLine1}, ${collection.address.city}, ${collection.address.postcode}`
            : 'No pickup information available'
          const to = delivery?.address
            ? `${delivery.address.addressLine1}, ${delivery.address.city}, ${delivery.address.postcode}`
            : booking?.statusLabel === BookingStatus.Complete
            ? 'Delivery Complete'
            : 'No pending deliveries'
          const colPackageIds = new Set(
            (collection.addressPackages ?? []).map((ap) => String(ap.package?.id)),
          )
          const pairedPackages = delivery
            ? (delivery.allocations ?? []).filter((al) => colPackageIds.has(String(al.package?.id)))
            : []
          const totalItems = delivery
            ? pairedPackages.length
            : collection.addressPackages?.length ?? 0
          const totalWeight = delivery
            ? pairedPackages.reduce((sum, al) => sum + (al.package?.weight ?? 0), 0)
            : (collection.addressPackages ?? []).reduce(
                (sum, ap) => sum + (ap.package?.weight ?? 0),
                0,
              )

          const isCollectionOnly =
            delivery === null && referenceAddressType === BookingAddressType.Collection

          return (
            <Box key={`${collection.id}-${delivery?.id ?? 'none'}`}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Box sx={contentContainerStyles}>
                {isCollectionOnly ? (
                  <Box sx={clickableRowStyles} onClick={onDetailsClick}>
                    <Box sx={detailRowContentStyles}>
                      <BusinessIcon sx={iconStyles} />
                      <Typography variant='body2' sx={textStyles}>
                        Pick up: {from}
                      </Typography>
                    </Box>
                    <ChevronRightIcon fontSize='large' sx={chevronStyles} />
                  </Box>
                ) : (
                  <>
                    {collection.address && (
                      <Box sx={detailRowStyles}>
                        <BusinessIcon sx={iconStyles} />
                        <Typography variant='body2' sx={textStyles}>
                          From: {from}
                        </Typography>
                      </Box>
                    )}

                    {!isCollectionOnly && (
                      <Box sx={clickableRowStyles} onClick={onDetailsClick}>
                        <Box sx={detailRowContentStyles}>
                          <LocationOnIcon sx={iconStyles} />
                          <Typography variant='body2' sx={textStyles}>
                            To: {to}
                          </Typography>
                        </Box>
                        {delivery && (delivery as any).signedAt ? (
                          <Typography
                            variant='caption'
                            sx={{
                              color: 'success.main',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                            }}>
                            Delivered ✓
                          </Typography>
                        ) : (
                          <ChevronRightIcon fontSize='large' sx={chevronStyles} />
                        )}
                      </Box>
                    )}
                  </>
                )}

                <Box sx={detailRowStyles}>
                  <LocalShippingIcon sx={iconStyles} />
                  <Typography variant='body2' sx={textStyles}>
                    {totalItems} items, {totalWeight} kg
                  </Typography>
                </Box>
                {delivery && !(delivery as any).signedAt && (
                  <Box sx={{ ...detailRowStyles, mt: 1 }}>
                    <AccessTimeIcon sx={iconStyles} />
                    <Typography variant='body2' sx={textStyles}>
                      {(() => {
                        const eta = addressEtas.find(
                          (e) => String(e.bookingAddressId) === String(delivery.id),
                        )

                        if (eta?.nextAddressETA && eta.etaWindowMinutes != null)
                          return `ETA: ${
                            calculateDeliveryWindow(eta.nextAddressETA, eta.etaWindowMinutes)
                              .timeRange
                          }`

                        return 'ETA not available'
                      })()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  const fromAddress = bookingCurrentAction?.address
    ? `${bookingCurrentAction.address.addressLine1}, ${bookingCurrentAction.address.city}, ${bookingCurrentAction.address.postcode}`
    : 'No pickup information available'

  const toAddress = nextBookingAction?.address
    ? `${nextBookingAction.address.addressLine1}, ${nextBookingAction.address.city}, ${nextBookingAction.address.postcode}`
    : booking?.statusLabel === BookingStatus.Complete
    ? 'Delivery Complete'
    : 'No pending deliveries'

  const nextAllocations = nextBookingAction?.allocations ?? []
  const nextAddressPkgs = nextBookingAction?.addressPackages ?? []
  const pickupPkgs = bookingCurrentAction?.addressPackages ?? []
  const pkgSource =
    nextAllocations.length > 0
      ? nextAllocations
      : nextAddressPkgs.length > 0
      ? nextAddressPkgs
      : pickupPkgs
  const totalItems = pkgSource.length
  const totalWeight = pkgSource.reduce((sum, p) => sum + (p.package?.weight ?? 0), 0)

  const isCollectionOnly =
    referenceAddressType === BookingAddressType.Collection && !nextBookingAction

  return (
    <Box sx={containerStyles}>
      <Typography variant='h6' sx={titleStyles}>
        Details
      </Typography>

      <Box sx={contentContainerStyles}>
        {isCollectionOnly ? (
          <Box sx={clickableRowStyles} onClick={onDetailsClick}>
            <Box sx={detailRowContentStyles}>
              <BusinessIcon sx={iconStyles} />
              <Typography variant='body2' sx={textStyles}>
                Pick up: {fromAddress}
              </Typography>
            </Box>
            <ChevronRightIcon fontSize='large' sx={chevronStyles} />
          </Box>
        ) : (
          <>
            {bookingCurrentAction?.address && (
              <Box sx={detailRowStyles}>
                <BusinessIcon sx={iconStyles} />
                <Typography variant='body2' sx={textStyles}>
                  From: {fromAddress}
                </Typography>
              </Box>
            )}

            <Box sx={clickableRowStyles} onClick={onDetailsClick}>
              <Box sx={detailRowContentStyles}>
                <LocationOnIcon sx={iconStyles} />
                <Typography variant='body2' sx={textStyles}>
                  To: {toAddress}
                </Typography>
              </Box>
              <ChevronRightIcon fontSize='large' sx={chevronStyles} />
            </Box>
          </>
        )}

        <Box sx={detailRowStyles}>
          <LocalShippingIcon sx={iconStyles} />
          <Typography variant='body2' sx={textStyles}>
            {totalItems} items, {totalWeight} kg
          </Typography>
        </Box>
        {nextBookingAction &&
          !(nextBookingAction as any).signedAt &&
          (nextBookingAction as any).type === BookingAddressType.Delivery && (
            <Box sx={{ ...detailRowStyles, mt: 1 }}>
              <AccessTimeIcon sx={iconStyles} />
              <Typography variant='body2' sx={textStyles}>
                {(() => {
                  const eta = addressEtas.find(
                    (e) => String(e.bookingAddressId) === String(nextBookingAction.id),
                  )

                  if (eta?.nextAddressETA && eta.etaWindowMinutes != null)
                    return `ETA: ${
                      calculateDeliveryWindow(eta.nextAddressETA, eta.etaWindowMinutes).timeRange
                    }`

                  return 'ETA not available'
                })()}
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  )
}
